import type { ErrorResponse } from "@types";
import axios, { AxiosRequestConfig } from "axios";
import auth from "../api/auth";
import {
  COOKIE_ACCESS_TOKEN_KEY,
  COOKIE_AREA_KEY,
  COOKIE_COUNTRY_KEY,
  COOKIE_LOCALE_KEY,
  COOKIE_LOGIN_TOKEN_KEY,
} from "@utils/constants";
import { getCookie, getLocale, isBot as serverBotCheck, getCachedClientId, getQueryAreaId } from "@utils/Cookies";
import { toast } from "react-toastify";
import { getNotifyConfig, isServer } from "@utils/helpers";
import getConfig from "next/config";
import { _computeHMAC } from "@utils/crypto";
import { AppGuid } from "@utils/guid";
import { isbot } from "isbot";
import { getClientIdFromGtag } from "@utils/gtag";

const { publicRuntimeConfig } = getConfig();

const client = axios.create({
  baseURL: publicRuntimeConfig.BASE_URL,
  responseType: "json",
});
client.defaults.timeout = 60000;
let interceptorId = -1;
export async function requestInterceptor(config: AxiosRequestConfig) {
  const Cookies = getCookie();
  const token = Cookies.get(COOKIE_ACCESS_TOKEN_KEY);
  const loginToken = Cookies.get(COOKIE_LOGIN_TOKEN_KEY);
  if (!config.headers) config.headers = {};
  config.headers["Accept-Language"] = (getLocale() || Cookies.get(COOKIE_LOCALE_KEY)).split(
    "-"
  )[0];
  const locale = getLocale() || Cookies.get(COOKIE_LOCALE_KEY);
  config.headers["lang"] = locale.split("-")[0];
  const localeCountry = locale && locale.includes('-') ? locale.split('-')[1] : null;
  config.headers["Country"] = Cookies.get(COOKIE_COUNTRY_KEY) || localeCountry || "KW";

  config.headers["x-token"] = loginToken ?? token ?? "";

  const allowBotHeaders = publicRuntimeConfig.ALLOW_BOT_HEADERS == 1;
  // make sure the header is set, and the areaId is not set because the bot might be requesting a specific area, and setting the isBot will tell the backend to choose a random area
  const isBot = (isServer() ? serverBotCheck() : navigator && navigator.userAgent && isbot(navigator.userAgent));
  const sendBotHeaders = isBot && allowBotHeaders && !Cookies.get(COOKIE_AREA_KEY);
  config.headers["areaId"] = !Cookies.get(COOKIE_AREA_KEY)? Number(getQueryAreaId()) || 0 : Cookies.get(COOKIE_AREA_KEY);
  sendBotHeaders && (config.headers["X-Bot"] = "true");

  return config;
}
client.interceptors.request.use(
  requestInterceptor,
  function (error) {
    return Promise.reject(error);
  }
);

export const fileDownloadClient = axios.create({
  baseURL: client.defaults.baseURL,
  timeout: client.defaults.timeout,
  responseType: 'blob',
  headers: {
    ...client.defaults.headers.common,
  }
});

fileDownloadClient.interceptors.request.use(requestInterceptor);

let refreshTokenPromise: Promise<string> | null = null;

const reGetToken = async (): Promise<string> => {
  const clientId = isServer() ? getCachedClientId() : await getClientIdFromGtag();

  return auth
    .deviceAuth(clientId)
    .fetch()
    .then((res) => {
      const token = res.data.access_token || "";
      getCookie().set(COOKIE_ACCESS_TOKEN_KEY, token, {}, 365 * 2);
      return Promise.resolve(token);
    })
    .catch((err: ErrorResponse) => {
      return Promise.reject(err);
    });
};

function isCorsMaintenanceError(error: any) {
  return (
    // Explicit 503 response (with CORS headers)
    error.response?.status === 503 ||

    // CORS-blocked error pattern
    (
      !error.response &&
      error.code !== 'ECONNABORTED' && // Not a timeout
      error.message === 'Network Error' && // Browser's generic CORS message
      navigator.onLine // User has internet connection
    )
  );
}

client.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (error) {
    const originalRequest = error.config;
    const Cookies = getCookie();
    const handleErrorResponse = () => {
      if ([502, 503, 504].includes(error?.response?.status)) {
        if (isServer()) return
        window?.location?.replace('/maintenance')
        return;
      }
      toast.error(error?.response?.data?.meta?.msg || "Something Went Wrong", getNotifyConfig());
    };

    if (error.response) {
      switch (error.response.status) {
        case 504:
        case 503:
        case 502:
        case 500:
          handleErrorResponse();
          break;
        case 400:
          handleErrorResponse();
          break;
        case 401:
          handleErrorResponse();
          break;
        case 404:
          handleErrorResponse();
          break;
        case 409:
          handleErrorResponse();
          Cookies.remove(COOKIE_COUNTRY_KEY);
          Cookies.remove(COOKIE_AREA_KEY);
          break;
        case 403:
          Cookies.remove(COOKIE_LOGIN_TOKEN_KEY);
          const refreshToken = Cookies.get(COOKIE_ACCESS_TOKEN_KEY);
          if (refreshToken) {
            if (!refreshTokenPromise) {
              refreshTokenPromise = reGetToken()
                .then((token) => token)
                .catch((err) => Promise.reject(err))
                .finally(() => {
                  refreshTokenPromise = null;
                });
            }
            return refreshTokenPromise
              .then(async (accessToken) => {
                const requestId = AppGuid.generateReqId();
                let toCheck = requestId + accessToken;

                client.defaults.headers.common["x-token"] = accessToken;
                originalRequest.headers["X-REQID"] = requestId;
                originalRequest.headers["X-HASH"] = await _computeHMAC(
                  toCheck,
                  publicRuntimeConfig.HASH_KEY2
                );
                return client(originalRequest);
              })
              .catch((err) => {
                if (err.error.code === 403) {
                  Cookies.remove(COOKIE_ACCESS_TOKEN_KEY);
                  Cookies.remove(COOKIE_LOGIN_TOKEN_KEY);
                }
                return Promise.reject(err.error.details ? err.error.details : err);
              });
          } else {
            if (!refreshTokenPromise) {
              refreshTokenPromise = reGetToken()
                .then((token) => token)
                .catch((err) => Promise.reject(err))
                .finally(() => {
                  refreshTokenPromise = null;
                });
            }
            return refreshTokenPromise
              .then(async (accessToken) => {
                const requestId = AppGuid.generateReqId();
                let toCheck = requestId + accessToken;

                client.defaults.headers.common["x-token"] = accessToken;
                originalRequest.headers["X-REQID"] = requestId;
                originalRequest.headers["X-HASH"] = await _computeHMAC(
                  toCheck,
                  publicRuntimeConfig.HASH_KEY2
                );
                return client(originalRequest);
              })
              .catch((err) => {
                if (err.error.code === 403) {
                  Cookies.remove(COOKIE_ACCESS_TOKEN_KEY);
                  Cookies.remove(COOKIE_LOGIN_TOKEN_KEY);
                }
                return Promise.reject(err.error.details ? err.error.details : err);
              });
          }
        default:
          break;
      }
    }
    if (isCorsMaintenanceError(error) && !isServer())
      window?.location?.replace('/maintenance')
    return Promise.reject(error);
  }
);

export function setClientAuthInterceptorCallback(callback?: () => void) {
  if (isServer() && interceptorId >= 0) return;
  if (interceptorId >= 0) client.interceptors.response.eject(interceptorId);
  interceptorId = client.interceptors.response.use(
    function (response) {
      return response;
    },
    function (error) {
      const Cookies = getCookie();
      if (error.response?.status === 401) {
        Cookies.remove(COOKIE_LOGIN_TOKEN_KEY);
        callback?.();
      }
      return Promise.reject(error);
    }
  );
}

setClientAuthInterceptorCallback();

export default client;
